import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { Loader, DistributionBar } from 'ui';
import { ESGDistributionCalculator } from 'modules/ESG';
import { parseToCurrency } from 'shared/functions';
import { useReportPage } from 'shared/hooks';

const useStyles = makeStyles((theme) => ({
  divider: {
    alignSelf: 'stretch',
    height: 'auto',
    margin: theme.spacing(0, 2),
  },
  legendRow: {
    fontSize: 18,
    fontWeight: 'bold',
    marginTop: 20,
    marginLeft: 50,
  },
  title: {
    paddingLeft: theme.spacing(2),
    fontSize: useReportPage ? 12 : 16,
  },
}));

const ESGImpactOverview = ({ data, loading }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    impactDistribution: {
      scoreCompanyFactor,
      scoreCompany,
      scoreSupplyChain,
      scoreDownstreamFactor,
    },
    overallMonetization: { externalCostsMillionsEur, externalCostsDividedByRevenue },
  } = data;

  const distributionProgress = (scoreCompanyFactor * 100).toFixed(1);
  const downstreamProgress = (scoreDownstreamFactor * 100).toFixed(1);
  const distributionScores = scoreCompany &&
    scoreSupplyChain && {
      scoreCompany,
      scoreSupplyChain,
      scoreDownstreamFactor,
    };

  return (
    <Paper>
      <Box p={2} pt={useReportPage ? 1 : 2}>
        <Typography variant="h6">{t('Impact summary')}</Typography>
        {loading ? (
          <Loader center />
        ) : (
          <>
            <Box py={2} pt={useReportPage ? 1 : 2}>
              <Divider />
            </Box>
            <Grid container>
              <Grid item xs={5}>
                <Box mb={2}>
                  <Typography variant="h6" className={classes.title}>
                    {t('Impact distribution')}
                  </Typography>
                </Box>

                <Box p={2}>
                  <DistributionBar
                    distributionScores={distributionScores}
                    distributionProgress={distributionProgress}
                    downstreamProgress={downstreamProgress}
                    labelA={t('Company Sites')}
                    labelB={t('Upstream')}
                    labelC={t('Downstream')}
                    isDouble
                  />
                </Box>
              </Grid>
              <Divider className={classes.divider} orientation="vertical" />
              <Grid item xs={6}>
                <Box mb={2}>
                  <Typography variant="h6" className={classes.title}>
                    {t('Overall monetisation')}
                  </Typography>
                </Box>
                <Box p={2} pb={1}>
                  <ESGDistributionCalculator
                    firstInputLabel={t('Total net externality per EUR revenue')}
                    firstInputValue={externalCostsDividedByRevenue.toFixed(2)}
                    secondInputLabel={t('Absolute net externality')}
                    secondInputValue={parseToCurrency(externalCostsMillionsEur)}
                    firstInputUnit="EUR"
                    secondInputUnit="mEUR"
                    showWeightBox={false}
                  />
                </Box>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </Paper>
  );
};

ESGImpactOverview.propTypes = {
  loading: PropTypes.bool.isRequired,
  data: PropTypes.shape({
    impactDistribution: PropTypes.object.isRequired,
    overallMonetization: PropTypes.object.isRequired,
  }).isRequired,
};

export default ESGImpactOverview;
